/**
 * components/locks/ConnectorMesh.tsx — Shared lock rendering (T14).
 *
 * Rendering logic common to ConnectorJoist and ConnectorHeader (PLAN §2.18):
 * - GLB available → cloned scene (GlbView, Suspense requested by parent),
 * rotated by component euler (`meshQuaternion` — FIX A: euler goes
 *Only to GLB body, holes/boxes DO NOT receive it).
 * - GLB absent → parametric FALLBACK box with module dims
 * (`module.thickness × module.height × module.width` in the authoring frame),
 * WITHOUT euler: the box is already in the group's auth frame (FIX A) → the height
 * of the module remains vertical in the world.
 * - Edges of the box in debug.
 *
 * Indagine incastro header/joist (25-09-2026, storia riassunta — i dettagli
 * completi sono in `transforms/connectorPlacement.ts`): il posizionamento
 * X/Y (flush sul piano trave) e l'orientamento (conversione autoring→viewer
 * con segno OPPOSTO per joist/header, mirror G3) sono risolti entrambi nel
 * layer `model/`+`transforms/`, sul quaternion/posizione dell'INTERO GRUPPO
 * — MAI qui sul solo corpo mesh. Tentativi precedenti che ricentravano o
 * ruotavano la mesh SOLO in questo componente disallineavano sistematicamente
 * le viti dai propri fori (`hole.position`, dato JSON solidale alla stessa
 * geometria rigida) — bug riprodotto in ogni configurazione. Questo
 * componente resta quindi MUTO: nessun calcolo di posizione/orientamento qui
 * (SPEC §6).*/

import { useEffect, useMemo } from "react";
import * as THREE from "three";
import type { ConnectorModule } from "../../data/types";
import { GlbView } from "./GlbView";
import { COLORS } from "../../config/colors";

// ─── Props interne ────────────────────────────────────────────

export interface ConnectorMeshProps {
  /** GLB URL blob; null → parametric box (R6 fallback).*/
  glbUrl: string | null;
  /** Module dims (from ConnectorInstance.module).*/
  module: ConnectorModule;
  /** Fallback box color (from config/colors.ts).*/
  color: string;
  /** Show fallback box edges (debug helper).*/
  showEdges?: boolean;
  /** Material opacity (0..1) — overlay componentsState.*/
  opacity?: number;
  /**
   * Component euler (component_rotation_deg) — applied ONLY to the GLB
   * (body). Holes and fallback box DO NOT receive it (FIX A: the normal
   * [0,0,1] points X and not Y; the height of the box remains vertical).*/
  meshQuaternion?: THREE.Quaternion;
}

//─── Component ─────────────────────── ────────────────────────

/**
 * Render the lock body: GLB (with euler) if available, otherwise the
 * parametric module box (without euler) with debug edges.*/
export function ConnectorMesh({ glbUrl, module, color, showEdges = false, opacity = 1, meshQuaternion }: ConnectorMeshProps) {
  if (glbUrl) {
    return (
      <group quaternion={meshQuaternion}>
        <GlbView url={glbUrl} />
      </group>
    );
  }

  return (
    <>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[module.thickness, module.height, module.width]} />
        <meshStandardMaterial
          color={color}
          roughness={0.5}
          metalness={0.6}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>
      {showEdges ? <EdgesBox args={[module.thickness, module.height, module.width]} /> : null}
    </>
  );
}

//─── Internal sub-component ─────────────────────────────────

/**
 * EdgesBox — fallback box outline lines (visual debug helper).
 * The geometry is recalculated only when the args change.*/
function EdgesBox({ args }: { args: [number, number, number] }) {
  // `args` e' un array letterale ricreato a ogni render del chiamante: la
  // memo deve dipendere dai tre valori, non dall'array, altrimenti la
  // geometria si ricrea (e si ricarica sulla GPU) a ogni render.
  const [x, y, z] = args;
  const edges = useMemo(() => {
    const box = new THREE.BoxGeometry(x, y, z);
    const result = new THREE.EdgesGeometry(box);
    box.dispose();
    return result;
  }, [x, y, z]);
  // Creata a mano (non dichiarata nel JSX): R3F non la libera da solo.
  useEffect(() => () => edges.dispose(), [edges]);
  return (
    <lineSegments geometry={edges}>
      <lineBasicMaterial color={COLORS.edge} transparent opacity={0.6} />
    </lineSegments>
  );
}
